import { useMemo, useState, type CSSProperties } from "react";
import { ExternalLink, Lock, Mail, MapPin, MessagesSquare, Phone, Search } from "lucide-react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { RESOURCES_DISCLAIMER, usePolicy } from "@/features/policies/policies";
import { useResources } from "@/features/resources/resources";
import { FacetSelect } from "@/components/FacetSelect";
import { MembersOnly } from "@/components/MembersOnly";
import { PreviewCutoff } from "@/components/PreviewCutoff";
import { RowListSkeleton } from "@/components/Skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/**
 * 252 entries: too many to scroll, too few to page. So the page is a search
 * box and three rows of facets, and the list underneath is flat.
 *
 * Adding a resource is not here. It lives beside My Profile in the identity
 * menu, the same as legacies — this page is for finding one.
 */

type Sort = "name" | "added";

export default function ResourcesPage() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [tag, setTag] = useState<string | null>(null);
  const [location, setLocation] = useState<string | null>(null);
  // Recently added, not A to Z: the library grows, and what is new is what a
  // returning member came back for. Todd's suggestion, agreed 2026-09-01.
  const [sort, setSort] = useState<Sort>("added");

  const { data, error } = useResources({
    search,
    category: category ?? undefined,
    tag: tag ?? undefined,
    location: location ?? undefined,
  });

  const resources = useMemo(() => {
    const rows = [...(data?.resources ?? [])];
    if (sort === "added") {
      // Newest first, and anything without a date last rather than pretending
      // to be 1970.
      rows.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
    }
    return rows;
  }, [data, sort]);

  // Not a member: a capped sample with the way to reach each entry withheld,
  // the same bargain the directory makes. Searching is off — a cap any term
  // can reach past is not a cap — so the box goes with it.
  const preview = Boolean(data?.preview);
  const filtered = Boolean(search || category || tag || location);
  const hidden = (data?.total ?? 0) - resources.length;

  const heading = (
    <div>
      <h1 className="text-2xl">Resources</h1>
      <p className="text-muted-foreground mt-1 text-sm">
        Guides, templates, and references for legacy stewardship, gathered by
        AFELL members since 2019.
      </p>
      <p className="text-muted-foreground mt-1 text-xs">
        Listings are recommended by members and are not vetted or endorsed by
        Soft Network. <ResourcesDisclaimer />
      </p>
    </div>
  );

  // Anything the server genuinely refuses — it should not, now that the
  // library previews — rather than a skeleton spinning for ever.
  if (error) {
    return (
      <div className="max-w-4xl">
        {heading}
        <MembersOnly what="The resource library" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      {heading}

      <div className="mt-6 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {!preview && (
            <div className="relative min-w-64 flex-1">
              <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                className="pl-9"
                placeholder="Search resources…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          <div className="flex gap-1">
            {(["name", "added"] as Sort[]).map((option) => (
              <Button
                key={option}
                size="sm"
                variant={sort === option ? "secondary" : "ghost"}
                onClick={() => setSort(option)}
              >
                {option === "name" ? "A–Z" : "Recently added"}
              </Button>
            ))}
          </div>
        </div>

        {/* Each axis disappears when it has nothing to offer — which is how
            the location filter is absent under Funding without anything having
            to know that Funding has no locations.

            One shape for everybody. The preview is meant to show what the page
            is, not to be a page of its own, so it gets the same three
            dropdowns opening to the same counts. Choosing one asks them to
            join instead of filtering, for the reason search is off there: the
            list is a capped sample, and an axis you can narrow is a way of
            walking past the cap a facet at a time. */}
        <div className="grid gap-2 sm:grid-cols-3">
          <FacetSelect
            label="Category"
            facets={data?.facets.categories ?? []}
            selected={category}
            onSelect={setCategory}
            preview={preview}
          />
          <FacetSelect
            label="Tag"
            facets={data?.facets.tags ?? []}
            selected={tag}
            onSelect={setTag}
            preview={preview}
          />
          <FacetSelect
            label="Geographic Location"
            facets={data?.facets.locations ?? []}
            selected={location}
            onSelect={setLocation}
            preview={preview}
          />
        </div>
      </div>

      {data && !preview && (
        <p className="text-muted-foreground mt-5 text-xs">
          {data.total} {data.total === 1 ? "resource" : "resources"}
          {filtered && data.total !== undefined ? " match" : ""}
          {filtered && (
            <button
              type="button"
              className="text-brand-ink ml-2 underline-offset-4 hover:underline"
              onClick={() => {
                setSearch("");
                setCategory(null);
                setTag(null);
                setLocation(null);
              }}
            >
              Clear filters
            </button>
          )}
        </p>
      )}

      <div className="mt-3 space-y-3">
        {data === undefined ? (
          <RowListSkeleton withAvatar={false} />
        ) : resources.length === 0 ? (
          <p className="animate-page-enter text-muted-foreground py-12 text-center">
            Nothing matches those filters.
          </p>
        ) : (
          resources.map((r, i) => {
            // Two reasons an entry can be shut, and they read differently: a
            // paid-only resource seen by a free member, and any resource seen
            // by somebody who is not a member yet. The second still shows what
            // the entry *is* — that is what makes it a preview.
            const gated = r.locked && !preview;
            return (
            <Card
              key={r.id}
              className="animate-item-enter"
              style={{ "--stagger": i } as CSSProperties}
            >
              <CardHeader>
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base">
                    {r.locked || !r.url ? (
                      // The padlock says it is shut; the title does not have
                      // to. Greying the whole line left an entry's title
                      // lighter than its own description (June, 2026-09-24).
                      <span className="flex items-center gap-1.5">
                        {r.locked && (
                          <Lock className="text-muted-foreground size-4 shrink-0" />
                        )}{" "}
                        {r.title}
                      </span>
                    ) : (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 underline-offset-4 hover:underline"
                      >
                        {r.title} <ExternalLink className="size-3.5" />
                      </a>
                    )}
                  </CardTitle>
                  <Badge variant="secondary">{r.category}</Badge>
                  {r.afell && <Badge variant="outline">Via AFELL</Badge>}
                  {r.access === "paid" && <Badge variant="outline">Paid members</Badge>}
                </div>
                <CardDescription>
                  {gated ? (
                    "Available to annual (paid) members. Upgrade your membership to access this resource."
                  ) : (
                    <Description text={r.description} />
                  )}
                </CardDescription>
                {!r.locked && (r.email || r.phone || r.location) && (
                  // A quarter of the library has no website: a name and a way
                  // to reach them is the whole entry.
                  <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    {r.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="size-3" /> {r.location}
                      </span>
                    )}
                    {r.email && (
                      <a
                        href={`mailto:${r.email}`}
                        className="inline-flex items-center gap-1 underline-offset-4 hover:underline"
                      >
                        <Mail className="size-3" /> {r.email}
                      </a>
                    )}
                    {r.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="size-3" /> {r.phone}
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {!gated &&
                    r.tags.map((t) => (
                      <Badge key={t} variant="outline" className="text-xs">
                        {t}
                      </Badge>
                    ))}
                  {r.date && (
                    // When it joined the library, not a deadline.
                    <span className="text-muted-foreground text-xs">
                      Added{" "}
                      {new Date(r.date).toLocaleDateString(undefined, {
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  )}
                  {r.thread && (
                    <Link
                      to={`/discussions/${r.thread.id}`}
                      className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs underline-offset-4 hover:underline"
                    >
                      <MessagesSquare className="size-3" />
                      From discussion: {r.thread.title}
                    </Link>
                  )}
                </div>
              </CardHeader>
            </Card>
            );
          })
        )}
      </div>

      <PreviewCutoff
        hidden={hidden}
        show={preview && data !== undefined}
        noun="resources"
        audience="members"
      />
    </div>
  );
}

/** How much of a description shows before it needs asking for. Long enough
 *  that most entries are unaffected, short enough that one scraped entry
 *  cannot push the next ten off the screen. */
const PREVIEW_CHARACTERS = 220;

/**
 * A description that does not run away with the page.
 *
 * These came out of Squarespace rich-text bodies and vary from a phrase to
 * several paragraphs, so a handful of entries set the height of every row
 * around them. Todd, after the 1 September demo: "truncate / show more on long
 * descriptions."
 *
 * Cut on a word boundary, and the button says which way it goes rather than
 * being an ellipsis somebody has to guess at.
 */
function Description({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const full = (text || "").trim();
  if (full.length <= PREVIEW_CHARACTERS) return <>{full}</>;

  const cut = full.slice(0, PREVIEW_CHARACTERS);
  const short = cut.slice(0, cut.lastIndexOf(" ") > 0 ? cut.lastIndexOf(" ") : cut.length);

  return (
    <>
      {open ? full : `${short}\u2026`}{" "}
      <button
        type="button"
        aria-expanded={open}
        className="text-foreground underline underline-offset-4"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        {open ? "Show less" : "Show more"}
      </button>
    </>
  );
}

/**
 * Soft Network's disclaimer and waiver of liability for the listings, which
 * sat above them on the old site (Chelsea, 2026-09-16). One quiet line on the
 * page and the whole text a click away: the point of a waiver is that it is
 * where the reliance happens, not that it is loud. The Terms of Use already
 * disclaim warranties and third-party links in general; this is the specific
 * part (recommended by members, not vetted, nobody paid to be listed).
 *
 * The text is a policy row, fetched only when opened.
 */
function ResourcesDisclaimer() {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className="underline underline-offset-4 hover:text-foreground">
          Read the disclaimer
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        {open && <DisclaimerText />}
      </DialogContent>
    </Dialog>
  );
}

function DisclaimerText() {
  const { data, error } = usePolicy(RESOURCES_DISCLAIMER);
  return (
    <>
      <DialogHeader>
        <DialogTitle>{data?.title ?? "Listings and References"}</DialogTitle>
        <DialogDescription className="sr-only">
          Disclaimer and waiver of liability for the resource listings
        </DialogDescription>
      </DialogHeader>
      {error ? (
        <p className="text-muted-foreground text-sm">
          The disclaimer could not be loaded. Please try again in a moment.
        </p>
      ) : data ? (
        <div className="prose prose-neutral prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown>{data.body}</ReactMarkdown>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">Loading…</p>
      )}
    </>
  );
}
